import { useState, useEffect } from "react";
import {
  useInfohubGetDocumentsQuery,
  useInfohubDeleteDocumentMutation,
  useInfohubUploadDocumentMutation,
} from "../../../lib/apollo/graphql/infohub/infohub-generated";
import { Document } from "../component/InfohubDocumentTable";

function useInfohubContainer() {
  const [currentPage, setCurrentPage] = useState(1);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  );

  const ITEMS_PER_PAGE = 20;

  // 문서 목록 조회
  const { loading, data, refetch } = useInfohubGetDocumentsQuery({
    variables: {
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      orderBy: "uploadedAt",
      order: "desc",
      includeDeleted: false,
    },
    onError: (error) => {
      console.error("❌ 문서 목록 조회 실패:", error);
    },
  });

  // 문서 업로드 mutation
  const [uploadDocumentMutation, { loading: uploadLoading }] =
    useInfohubUploadDocumentMutation({
      onCompleted: () => {
        refetch();
        setIsUploadModalOpen(false);
      },
      onError: (error) => {
        console.error("❌ 문서 업로드 실패:", error);
      },
    });

  // 문서 삭제 mutation
  const [deleteDocumentMutation, { loading: deleteLoading }] =
    useInfohubDeleteDocumentMutation({
      onCompleted: () => {
        refetch();
        setIsDeleteModalOpen(false);
        setSelectedDocument(null);
      },
      onError: (error) => {
        console.error("Delete error:", error);
      },
    });

  // 문서 데이터 변환
  const documents: Document[] =
    data?.documents?.documents?.map((doc) => ({
      id: doc.id,
      fileName: doc.originalFilename,
      uploadDate: formatUploadDate(doc.uploadedAt),
      uploadedBy: doc.uploadedBy,
      fileSize: formatFileSize(doc.sizeInBytes),
      status: mapProcessingStatus(doc.extractionStatus?.processingStatus),
      completionPercentage: doc.extractionStatus?.completionPercentage ?? 0,
      errorMessage: doc.extractionStatus?.error || undefined,
    })) || [];

  const totalPages = data?.documents?.totalPages || 1;

  // 업로드 날짜 포맷팅 (yy.MM.dd HH:mm)
  function formatUploadDate(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}.${month}.${day} ${hours}:${minutes}`;
  }

  // 파일 크기 포맷팅
  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  }

  // 처리 상태 매핑
  function mapProcessingStatus(
    status?: string
  ):
    | "PENDING"
    | "EXTRACTING_TEXT"
    | "GENERATING_CHUNKS"
    | "GENERATING_EMBEDDINGS"
    | "COMPLETED"
    | "FAILED" {
    if (!status) return "PENDING";

    switch (status.toUpperCase()) {
      case "PENDING":
        return "PENDING";
      case "TEXT_EXTRACTION":
      case "EXTRACTING_TEXT":
        return "EXTRACTING_TEXT";
      case "CHUNKING":
      case "GENERATING_CHUNKS":
        return "GENERATING_CHUNKS";
      case "EMBEDDING":
      case "EMBEDDINGS":
      case "GENERATING_EMBEDDINGS":
        return "GENERATING_EMBEDDINGS";
      case "COMPLETED":
        return "COMPLETED";
      case "FAILED":
        return "FAILED";
      default:
        return "PENDING";
    }
  }

  // 업로드 핸들러
  async function handleUpload(file: File) {
    try {
      // 메타데이터 구성
      const metadata = JSON.stringify({
        originalFilename: file.name,
        mimeType: file.type || "application/pdf",
        sizeInBytes: file.size,
      });

      // 업로드 mutation 실행 - File 객체를 직접 전달
      await uploadDocumentMutation({
        variables: {
          file: file, // File 객체를 직접 전달 (apollo-upload-client가 처리)
          metadata,
        },
      });
    } catch (error: any) {
      console.error("❌ 파일 업로드 중 오류 발생:", error);
      throw error;
    }
  }

  // 삭제 핸들러
  async function handleDelete() {
    if (!selectedDocument) return;

    await deleteDocumentMutation({
      variables: {
        id: selectedDocument.id,
      },
    });
  }

  // 삭제 버튼 클릭
  function handleDeleteClick(documentId: string) {
    const document = documents.find((doc) => doc.id === documentId);
    if (document) {
      setSelectedDocument(document);
      setIsDeleteModalOpen(true);
    }
  }

  // 페이지 변경
  function handlePageChange(page: number) {
    setCurrentPage(page);
  }

  // 처리 중인 문서가 있을 때 자동 새로고침 (5초마다)
  useEffect(() => {
    const hasProcessingDocuments = data?.documents?.documents?.some(
      (doc) =>
        doc.extractionStatus?.processingStatus === "TEXT_EXTRACTION" ||
        doc.extractionStatus?.processingStatus === "EXTRACTING_TEXT" ||
        doc.extractionStatus?.processingStatus === "CHUNKING" ||
        doc.extractionStatus?.processingStatus === "GENERATING_CHUNKS" ||
        doc.extractionStatus?.processingStatus === "EMBEDDING" ||
        doc.extractionStatus?.processingStatus === "EMBEDDINGS" ||
        doc.extractionStatus?.processingStatus === "GENERATING_EMBEDDINGS"
    );

    if (hasProcessingDocuments) {
      const intervalId = setInterval(() => {
        refetch();
      }, 5000); // 5초마다 새로고침

      return () => clearInterval(intervalId);
    }
  }, [data, refetch]);

  return {
    models: {
      documents,
      loading,
      uploadLoading,
      deleteLoading,
      currentPage,
      totalPages,
      isUploadModalOpen,
      isDeleteModalOpen,
      selectedDocument,
    },
    operations: {
      handleUpload,
      handleDelete,
      handleDeleteClick,
      handlePageChange,
      setIsUploadModalOpen,
      setIsDeleteModalOpen,
      setSelectedDocument,
    },
  };
}

export default useInfohubContainer;
